import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const TermsOfUse = () => {
  const [activeSection, setActiveSection] = useState('');
  const navigate = useNavigate();

  const sections = [
    { id: 'acceptance', title: 'Acceptance of Terms', icon: '✅' },
    { id: 'use-application', title: 'Use of the Application', icon: '⚙️' },
    { id: 'account', title: 'Account Registration', icon: '👤' },
    { id: 'contact', title: 'Contact Information', icon: '📞' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Navigation Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/login')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-medium">Back to Login</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-bold text-gray-900">Terms of Use</h1>
            </div>
            <div className="flex items-center space-x-2">
              <Link 
                to="/privacy-policy" 
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Table of Contents */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">📋</span>
                  Table of Contents
                </h3>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 flex items-center ${
                        activeSection === section.id
                          ? 'bg-blue-100 text-blue-700 font-medium border-l-4 border-blue-500'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-2">{section.icon}</span>
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-8">
            {/* Header Section */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -translate-y-20 translate-x-20"></div>
              <div className="relative">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 rounded-xl">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      Terms of Use
                    </h1>
                    <p className="text-gray-600 mt-2">Please read these terms carefully before using our service</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 bg-blue-50 px-6 py-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h6m-6 0v12a2 2 0 002 2h4a2 2 0 002-2V7m-6 0V6" />
                    </svg>
                    <span className="text-blue-900 font-semibold">Effective Date:</span>
                  </div>
                  <span className="text-blue-700 font-medium">March 1, 2025</span>
                </div>
              </div>
            </div>

            {/* Section 1: Acceptance of Terms */}
            <section id="acceptance" className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-green-100 text-green-700 p-3 rounded-xl">
                  <span className="text-2xl">✅</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Acceptance of Terms</h2>
                  <p className="text-gray-600 mt-1">Legal agreement and user responsibilities</p>
                </div>
              </div>
              <div className="ml-16 space-y-4">
                <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-green-500">
                  <p className="text-gray-700 leading-relaxed">
                    By accessing or using the Application, you confirm that you are at least
                    <span className="font-semibold text-green-700"> 18 years old</span> and have the legal capacity to enter into a binding
                    agreement. If you are using the Application on behalf of a business
                    entity, you represent that you have the authority to bind the entity to
                    these Terms.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 2: Use of the Application */}
            <section id="use-application" className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-blue-100 text-blue-700 p-3 rounded-xl">
                  <span className="text-2xl">⚙️</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Use of the Application</h2>
                  <p className="text-gray-600 mt-1">Guidelines for proper usage</p>
                </div>
              </div>
              <div className="ml-16 space-y-4">
                <div className="grid gap-4">
                  <div className="flex items-start space-x-4 bg-blue-50 p-4 rounded-lg">
                    <div className="bg-blue-200 p-2 rounded-full">
                      <span className="text-blue-800">🏢</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900">Business Use</h4>
                      <p className="text-gray-700 text-sm">
                        The Application is intended for use by registered business entities
                        involved in the buying and selling of products.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 bg-purple-50 p-4 rounded-lg">
                    <div className="bg-purple-200 p-2 rounded-full">
                      <span className="text-purple-800">📜</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-purple-900">Lawful Use</h4>
                      <p className="text-gray-700 text-sm">
                        You agree to use the Application only for lawful purposes and in
                        compliance with all applicable laws and regulations.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 bg-red-50 p-4 rounded-lg">
                    <div className="bg-red-200 p-2 rounded-full">
                      <span className="text-red-800">🚫</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-red-900">Prohibited Activities</h4>
                      <p className="text-gray-700 text-sm">
                        You must not misuse, interfere with, or disrupt the integrity of the
                        Application.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 3: Account Registration */}
            <section id="account" className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-orange-100 text-orange-700 p-3 rounded-xl">
                  <span className="text-2xl">👤</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Account Registration</h2>
                  <p className="text-gray-600 mt-1">Account security and responsibilities</p>
                </div>
              </div>
              <div className="ml-16 space-y-4">
                <div className="bg-orange-50 p-6 rounded-xl">
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <div className="bg-orange-200 p-1 rounded-full mt-1">
                        <svg className="w-3 h-3 text-orange-800" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700">
                        To access certain features, you may need to create an account.
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="bg-orange-200 p-1 rounded-full mt-1">
                        <svg className="w-3 h-3 text-orange-800" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700">
                        You are responsible for maintaining the confidentiality of your
                        account credentials.
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="bg-orange-200 p-1 rounded-full mt-1">
                        <svg className="w-3 h-3 text-orange-800" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700">
                        We reserve the right to suspend or terminate your account if we
                        suspect unauthorized or fraudulent activity.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 4: Contact Information */}
            <section id="contact" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8 shadow-xl">
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-white/20 p-3 rounded-xl">
                  <span className="text-2xl">📞</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Contact Information</h2>
                  <p className="text-blue-100 mt-1">Get in touch with us</p>
                </div>
              </div>
              <div className="ml-16">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                  <p className="text-blue-100 mb-4 font-medium">For any questions regarding these Terms:</p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="bg-white/20 p-2 rounded-lg">
                        <span className="text-xl">🏢</span>
                      </div>
                      <div className="text-white">
                        <p className="font-semibold">Star Solutions Private Limited</p>
                        <p className="text-sm text-blue-100">
                          84, Bylane No 6, Borthakur Mill Road,<br/>
                          Ulubari, Guwahati - 781007,<br/>
                          Assam, India
                        </p>
                      </div>
                    </div>
                    <a 
                      href="mailto:info@starsolutions.biz" 
                      className="flex items-center space-x-3 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors"
                    >
                      <div className="bg-white/20 p-2 rounded-lg">
                        <span className="text-xl">📧</span>
                      </div>
                      <span className="font-medium underline">info@starsolutions.biz</span>
                    </a>
                    <a 
                      href="tel:+918133047257" 
                      className="flex items-center space-x-3 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors"
                    >
                      <div className="bg-white/20 p-2 rounded-lg">
                        <span className="text-xl">📱</span>
                      </div>
                      <span className="font-medium">+91 8133047257</span>
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Final Acknowledgement */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-dashed border-gray-300 text-center">
              <div className="max-w-2xl mx-auto">
                <div className="bg-yellow-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">⚖️</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Legal Acknowledgement</h3>
                <p className="text-gray-700 italic">
                  By using the Application, you acknowledge that you have read,
                  understood, and agreed to these Terms of Use.
                </p>
              </div>
            </div>

            {/* Navigation Footer */}
            <div className="flex justify-between items-center bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <button
                onClick={() => navigate('/login')}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Login</span>
              </button>
              <Link
                to="/privacy-policy"
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <span>Privacy Policy</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;